import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Tv as TvIcon, Radio, ExternalLink, Calendar, Play, X } from "lucide-react";
import { toYouTubeEmbed, isYouTubeUrl, extractYouTubeId } from "@/lib/youtube";

export default function TVPage() {
  const [live, setLive] = useState(null);
  const [streams, setStreams] = useState([]);
  const [programs, setPrograms] = useState([]);
  // Modal state — when a program has a YouTube URL and the viewer taps the
  // card we open an in-page lightbox with the embedded player. Keeps users
  // on the TV page instead of punting them to the YouTube app.
  const [playing, setPlaying] = useState(null);
  useEffect(() => {
    api.get("/tv/current").then((r) => setLive(r.data));
    api.get("/tv/live").then((r) => setStreams(r.data || []));
    api.get("/tv/programs").then((r) => setPrograms(r.data || []));
  }, []);

  // Close the lightbox when the user hits Escape so touch and keyboard
  // interactions both feel natural.
  useEffect(() => {
    if (!playing) return;
    const onKey = (e) => { if (e.key === "Escape") setPlaying(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [playing]);

  const embedSrc = live ? (
    live.stream_type === "youtube" || isYouTubeUrl(live.stream_url)
      ? toYouTubeEmbed(live.stream_url)
      : live.stream_url
  ) : "";

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="flex items-center gap-2 mb-3">
        <TvIcon className="w-7 h-7 text-[#e11d2a]" />
        <h1 className="text-2xl md:text-3xl font-black">SİNE81 TV</h1>
        {live?.is_live && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#e11d2a] text-white text-xs rounded-md"><span className="w-2 h-2 bg-white rounded-full animate-pulse" /> CANLI</span>}
      </div>
      {live ? (
        <>
          <div className="aspect-video bg-black rounded-lg overflow-hidden mb-2">
            <iframe
              src={embedSrc}
              title={live.title}
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-bold">{live.title}</h2>
              {live.description && <p className="text-sm text-gray-600">{live.description}</p>}
            </div>
            {live.stream_url && (
              <a href={live.stream_url} target="_blank" rel="noreferrer" className="text-xs text-[#e11d2a] inline-flex items-center gap-1">
                <ExternalLink className="w-3.5 h-3.5" /> Kaynak
              </a>
            )}
          </div>
        </>
      ) : (
        <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center"><Radio className="w-12 h-12 text-gray-400" /></div>
      )}

      {streams.length > 1 && (
        <div className="mt-4">
          <h3 className="font-bold mb-2">Diğer Yayınlar</h3>
          <div className="grid md:grid-cols-3 gap-3">
            {streams.filter((s) => s.id !== live?.id).map((s) => (
              <div key={s.id} className="bg-white border border-gray-100 rounded-lg p-3">
                <div className="font-semibold text-sm">{s.title}</div>
                <div className="text-xs text-gray-500 mt-0.5">{s.stream_type}{s.is_live ? " • CANLI" : ""}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <h2 className="text-xl font-bold mt-8 mb-3 flex items-center gap-2"><Calendar className="w-5 h-5 text-[#e11d2a]" /> Programlar</h2>
      <div className="grid md:grid-cols-3 gap-4" data-testid="tv-programs-grid">
        {programs.map((p) => {
          // Build the thumbnail URL: prefer the admin-uploaded image, then
          // fall back to YouTube's auto-generated thumbnail so admins don't
          // have to curate a cover image for every program.
          const ytId = extractYouTubeId(p.youtube_url || "");
          const thumb = p.image || (ytId ? `https://i.ytimg.com/vi/${ytId}/hqdefault.jpg` : null);
          const playable = !!p.youtube_url;
          const CardTag = playable ? "button" : "div";
          const cardProps = playable
            ? {
                type: "button",
                onClick: () => setPlaying(p),
                "data-testid": `tv-program-play-${p.id}`,
                "aria-label": `${p.title} — oynat`,
              }
            : { "data-testid": `tv-program-${p.id}` };
          return (
            <CardTag
              key={p.id}
              {...cardProps}
              className={`bg-white border border-gray-100 rounded-lg overflow-hidden text-left group transition ${
                playable ? "hover:shadow-lg hover:-translate-y-0.5 cursor-pointer" : ""
              }`}
            >
              {thumb ? (
                <div className="relative aspect-video bg-black overflow-hidden">
                  <img
                    src={thumb}
                    alt={p.title}
                    className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
                    loading="lazy"
                  />
                  {playable && (
                    <>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-black/0 group-hover:from-black/70 transition" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="w-14 h-14 rounded-full bg-[#e11d2a] text-white flex items-center justify-center shadow-xl group-hover:scale-110 transition">
                          <Play className="w-7 h-7" fill="currentColor" />
                        </span>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="aspect-video bg-gray-100 flex items-center justify-center text-gray-400">
                  <TvIcon className="w-10 h-10" />
                </div>
              )}
              <div className="p-4">
                <h3 className="font-bold line-clamp-2">{p.title}</h3>
                {p.description && (
                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">{p.description}</p>
                )}
                {p.schedule_day && (
                  <div className="text-xs mt-2 text-[#e11d2a] font-semibold">
                    {p.schedule_day} {p.schedule_time}
                  </div>
                )}
              </div>
            </CardTag>
          );
        })}
        {programs.length === 0 && <div className="text-gray-500 text-sm col-span-3">Henüz program eklenmemiş.</div>}
      </div>

      {/* Lightbox player — fixed overlay with an embedded YouTube iframe.
       * `?autoplay=1` so the viewer doesn't need an extra tap after the
       * modal opens. The iframe gets a fresh key when the program changes
       * so React tears it down and rebuilds cleanly. */}
      {playing && (
        <div
          className="fixed inset-0 z-[1000] bg-black/85 flex items-center justify-center p-3"
          role="dialog"
          aria-modal="true"
          aria-label={playing.title}
          onClick={() => setPlaying(null)}
          data-testid="tv-program-lightbox"
        >
          <div
            className="relative w-full max-w-4xl bg-black rounded-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setPlaying(null)}
              className="absolute -top-10 right-0 md:top-2 md:right-2 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
              aria-label="Kapat"
              data-testid="tv-program-lightbox-close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="aspect-video">
              <iframe
                key={playing.id}
                src={`${toYouTubeEmbed(playing.youtube_url)}${toYouTubeEmbed(playing.youtube_url).includes("?") ? "&" : "?"}autoplay=1`}
                title={playing.title}
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <div className="p-4 bg-white">
              <h3 className="font-bold text-lg">{playing.title}</h3>
              {playing.description && (
                <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{playing.description}</p>
              )}
              {playing.schedule_day && (
                <div className="text-xs mt-2 text-[#e11d2a] font-semibold">
                  Yayın saati: {playing.schedule_day} {playing.schedule_time}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
